import React, { useState, useEffect } from "react";
import { Container, Card, Button, Form, Alert, Spinner } from "react-bootstrap";
import axios from "axios";
import { FaUpload, FaTrash, FaImage } from "react-icons/fa";

const OfferBanner = () => {
    const [image, setImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [currentBanner, setCurrentBanner] = useState("");
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [message, setMessage] = useState({ show: false, text: "", variant: "" });

    const API_URL = "https://madhusewingmachines.com/api/sales";

    useEffect(() => {
        fetchCurrentBanner();
    }, []);

    const fetchCurrentBanner = async () => {
        try {
            setLoading(true);
            const response = await axios.get(API_URL);
            if (response.data.success && response.data.imageUrl) {
                const imageUrl = response.data.imageUrl;
                const fullImageUrl = imageUrl.startsWith("http")
                    ? imageUrl
                    : `https://madhusewingmachines.com${imageUrl}`; // Add this line

                setCurrentBanner(fullImageUrl); // Use full URL
                setPreviewUrl("");
            }
        } catch (error) {
            showMessage("Failed to fetch current banner", "danger");
        } finally {
            setLoading(false);
        }
    };
    

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                showMessage("Please select an image file", "warning");
                return;
            }
            setImage(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleUpload = async () => {
        if (!image) {
            showMessage("Please select an image first", "warning");
            return;
        }

        try { 
            setUploading(true);
            const formData = new FormData();
            formData.append("image", image);

            const response = await axios.post(API_URL, formData, {
                headers: {
                    "Content-Type": "multipart/form-data"
                }
            });

            if (response.data.success) {
                showMessage("Banner uploaded successfully!", "success");
                setCurrentBanner(response.data.imageUrl);
                setImage(null);
                setPreviewUrl("");
            }
        } catch (error) {
            showMessage(error.response?.data?.message || "Failed to upload banner", "danger");
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("Are you sure you want to delete the current banner?")) return;

        try {
            setDeleting(true);
            const response = await axios.delete(API_URL);
            if (response.data.success) {
                showMessage("Banner deleted successfully", "success");
                setCurrentBanner("");
            }
        } catch (error) {
            showMessage(error.response?.data?.message || "Failed to delete banner", "danger");
        } finally {
            setDeleting(false);
        }
    };

    const showMessage = (text, variant) => {
        setMessage({ show: true, text, variant });
        setTimeout(() => setMessage({ show: false, text: "", variant: "" }), 5000);
    };

    const displayImage = previewUrl || currentBanner;

    return (
        <Container className="py-4">
            <h2 className="text-center mb-4">Sales Banner Management</h2>

            {message.show && (
                <Alert
                    variant={message.variant}
                    onClose={() => setMessage({ show: false, text: "", variant: "" })}
                    dismissible
                    className="mt-3"
                >
                    {message.text}
                </Alert>
            )}

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Loading banner...</p>
                </div>
            ) : (
                <Card className="shadow">
                    <Card.Body>
                        <Form>
                            <Form.Group className="mb-4">
                                <Form.Label>Upload New Banner Image</Form.Label>
                                <Form.Control
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    disabled={uploading || deleting}
                                />
                                <Form.Text className="text-muted">
                                    Recommended size: 1200x400px (JPG, PNG, max 5MB)
                                </Form.Text>
                            </Form.Group>

                            <div className="d-flex justify-content-between">
                                <Button
                                    variant="primary"
                                    onClick={handleUpload}
                                    disabled={!image || uploading || deleting}
                                >
                                    {uploading ? (
                                        <>
                                            <Spinner as="span" animation="border" size="sm" />
                                            <span className="ms-2">Uploading...</span>
                                        </>
                                    ) : (
                                        <>
                                            <FaUpload className="me-2" />
                                            {currentBanner ? "Replace Banner" : "Upload Banner"}
                                        </>
                                    )}
                                </Button>

                                {currentBanner && !previewUrl && (
                                    <Button
                                        variant="danger"
                                        onClick={handleDelete}
                                        disabled={uploading || deleting}
                                    >
                                        {deleting ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" />
                                                <span className="ms-2">Deleting...</span>
                                            </>
                                        ) : (
                                            <>
                                                <FaTrash className="me-2" />
                                                Delete Banner
                                            </>
                                        )}
                                    </Button>
                                )}
                            </div>
                        </Form>

                        {(displayImage || !currentBanner) && (
                            <div className="mt-4">
                                <h5 className="mb-3">
                                    {previewUrl ? "New Banner Preview" : currentBanner ? "Current Banner" : "No Banner Set"}
                                </h5>
                                {displayImage ? (
                                    <div className="border rounded p-2 bg-light">
                                        <img
                                            src={displayImage}
                                            alt="Banner preview"
                                            className="img-fluid w-100"
                                            style={{ maxHeight: "300px", objectFit: "contain" }}
                                        />
                                    </div>
                                ) : (
                                    <div className="text-center py-5 bg-light rounded">
                                        <FaImage size={48} className="text-muted mb-3" />
                                        <p className="text-muted">No banner image to display</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}
        </Container>
    );
};

export default OfferBanner;